import type { BusinessSubscriptionAccess } from '@neogeniux/api-contracts';
import {
  AddonInfo,
  BusinessSubscription,
  CreateSubscriptionPaymentDto,
  PaginatedResponse,
  PaymentFailureStatus,
  PaypalCaptureOrderDto,
  PaypalCreateOrderDto,
  PaypalCreateOrderResponseDto,
  PlanUsage,
  PurchaseAddonDto,
  PurchaseAddonResponse,
  RetryPaymentResponse,
  StripeDefaultPaymentMethodSummaryDto,
  StripePayDto,
  StripeSetDefaultPaymentMethodDto,
  StripeSetupIntentResponseDto,
  SubscriptionPayment,
  SubscriptionPaymentsParams,
  TrialStatus,
  UpdateBusinessSubscriptionDto,
  UpdateMySubscriptionDto,
  UpdateMySubscriptionResponse,
} from '@app/shared/models/subscription.model';
import { subscriptionService } from '@app/shared/services/subscription.service';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NEXT_INVOICE_PREVIEW_KEY } from './next-invoice-preview.hook';
// Canonical effective resolver (userBranch fallback + cross-tenant
// validation) — NOT the store's low-level wrapper, so plan-usage keys on the
// same branch id every other hook + the api.service auto-inject use.
import { useBranchOfficeId } from '@app/shared/hooks/use-branch-office-id';
import { useAuthStore } from '@app/shared/store/auth.store';
import { Roles } from '@app/shared/constants/roles.constant';

// Auth store persists `user.role` as either the string form
// ('admin', 'super_admin', …) or the nested `{ name }` shape
// depending on which endpoint hydrated it. Collapse to the string.
const resolveRoleName = (
  user: { role?: { name?: string } | string | null } | null | undefined
): string | undefined => {
  const role = user?.role;
  if (!role) return undefined;
  return typeof role === 'string' ? role : role.name ?? undefined;
};

export {
  SubscriptionStatus,
  SubscriptionPaymentStatus,
  SubscriptionPaymentMethod,
} from '@app/shared/models/subscription.model';
export type {
  BusinessSubscription,
  CreateSubscriptionPaymentDto,
  PaginatedResponse,
  PaymentFailureStatus,
  PlanLimits,
  PlanUsage,
  RetryPaymentResponse,
  SubscriptionPayment,
  SubscriptionPaymentsParams,
  TrialStatus,
  UpdateBusinessSubscriptionDto,
  UpdateMySubscriptionDto,
} from '@app/shared/models/subscription.model';

export const subscriptionKeys = {
  all: ['subscription'] as const,
  // `me` shape: the request goes to /subscription/me but the response
  // is scoped to the authenticated business. Include the businessId
  // in the cache key so an already-cached "me" from one identity
  // (sadmin, or a previous impersonation session) cannot paint into
  // the next identity. Callers pass the current auth store's
  // `user.businessId`; no-arg legacy overload is preserved for
  // invalidation predicates that want to hit every scope.
  me: (businessId?: string) =>
    businessId
      ? ([...subscriptionKeys.all, 'me', businessId] as const)
      : ([...subscriptionKeys.all, 'me'] as const),
  myAccess: () => [...subscriptionKeys.me(), 'access'] as const,
  myTrialStatus: () => [...subscriptionKeys.me(), 'trial-status'] as const,
  myPlanUsageBase: () => [...subscriptionKeys.me(), 'plan-usage'] as const,
  myPlanUsage: (branchOfficeId?: string) =>
    [...subscriptionKeys.me(), 'plan-usage', branchOfficeId ?? 'all'] as const,
  myStripeDefaultPaymentMethod: () =>
    [...subscriptionKeys.me(), 'stripe', 'default-payment-method'] as const,
  myPayments: (params: SubscriptionPaymentsParams) =>
    [...subscriptionKeys.me(), 'payments', params] as const,
  myPaymentStatus: () => [...subscriptionKeys.me(), 'payment-status'] as const,
  business: (businessId: string) =>
    [...subscriptionKeys.all, 'business', businessId] as const,
  businessStripeDefaultPaymentMethod: (businessId: string) =>
    [
      ...subscriptionKeys.business(businessId),
      'stripe',
      'default-payment-method',
    ] as const,
  businessPayments: (businessId: string, params: SubscriptionPaymentsParams) =>
    [...subscriptionKeys.business(businessId), 'payments', params] as const,
  businessAddons: (businessId: string) =>
    [...subscriptionKeys.business(businessId), 'addons'] as const,
};

export const useMySubscription = () => {
  const businessId = useAuthStore((s) => s.user?.businessId);
  return useQuery<BusinessSubscription>({
    queryKey: subscriptionKeys.me(businessId),
    queryFn: () => subscriptionService.getMySubscription(),
    enabled: !!businessId,
  });
};

export const useMySubscriptionItems = () => {
  return useQuery({
    queryKey: [...subscriptionKeys.me(), 'items'] as const,
    queryFn: () => subscriptionService.getMySubscriptionItems(),
    select: (data) => (Array.isArray(data) ? data : []),
    staleTime: 60 * 1000,
  });
};

export const useMyTrialStatus = () => {
  return useQuery<TrialStatus>({
    queryKey: subscriptionKeys.myTrialStatus(),
    queryFn: () => subscriptionService.getMyTrialStatus(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useMyAccessStatus = () => {
  return useQuery<BusinessSubscriptionAccess>({
    queryKey: subscriptionKeys.myAccess(),
    queryFn: () => subscriptionService.getMyAccessStatus(),
    staleTime: 60 * 1000, // 1 minute — verdict can change with billing events
  });
};

export const useMyPlanUsage = () => {
  const { branchOfficeId } = useBranchOfficeId();
  return useQuery<PlanUsage>({
    queryKey: subscriptionKeys.myPlanUsage(branchOfficeId),
    queryFn: () => subscriptionService.getMyPlanUsage(branchOfficeId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useUpdateMySubscription = () => {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateMySubscriptionResponse,
    Error,
    UpdateMySubscriptionDto
  >({
    mutationFn: (data) => subscriptionService.updateMySubscription(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      queryClient.invalidateQueries({
        queryKey: subscriptionKeys.myPaymentStatus(),
      });

      // Payments are recorded asynchronously via webhook, so refetch after a short delay
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      }, 3000);
    },
  });
};

export const useSetPreferredPaymentProvider = () => {
  const queryClient = useQueryClient();

  return useMutation<BusinessSubscription, Error, 'stripe' | 'paypal'>({
    mutationFn: (provider) =>
      subscriptionService.updateMySubscription({
        preferredPaymentProvider: provider,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
    },
  });
};

export const useMySubscriptionPayments = (
  params: SubscriptionPaymentsParams,
  options?: { enabled?: boolean }
) => {
  // Backend `SubscriptionService.assertBusinessAdmin` checks role
  // is EXACTLY 'admin' — super_admin does NOT pass. Gating on the
  // `billing.self.manage` permission is not enough because sadmin
  // inherits every permission via the super_admin-all-perms
  // strategy. When sadmin returns from impersonation the /account
  // hooks refetch with the sadmin JWT and 403. Gate on the concrete
  // role so sadmin skips the query entirely across every surface.
  const isBusinessAdmin = useAuthStore(
    (state) => resolveRoleName(state.user) === Roles.Admin
  );
  return useQuery<PaginatedResponse<SubscriptionPayment>>({
    queryKey: subscriptionKeys.myPayments(params),
    queryFn: () => subscriptionService.getMyPayments(params),
    enabled: isBusinessAdmin && (options?.enabled ?? true),
    select: (data) =>
      data && Array.isArray(data.items)
        ? data
        : {
            items: [],
            total: 0,
            page: params.page ?? 1,
            limit: params.limit ?? 20,
            totalPages: 0,
          },
  });
};

export const useCreateMyStripeSetupIntent = () => {
  return useMutation<StripeSetupIntentResponseDto, Error, void>({
    mutationFn: () => subscriptionService.createMyStripeSetupIntent(),
  });
};

export const useMyStripeDefaultPaymentMethodSummary = (enabled = true) => {
  // Same role-gate as `useMySubscriptionPayments` — backend
  // `assertBusinessAdmin` rejects super_admin, so gate on the
  // concrete 'admin' role rather than a permission the sadmin
  // inherits.
  const isBusinessAdmin = useAuthStore(
    (state) => resolveRoleName(state.user) === Roles.Admin
  );
  return useQuery<StripeDefaultPaymentMethodSummaryDto | null>({
    queryKey: subscriptionKeys.myStripeDefaultPaymentMethod(),
    queryFn: () => subscriptionService.getMyStripeDefaultPaymentMethodSummary(),
    enabled: isBusinessAdmin && enabled,
  });
};

export const useSetMyStripeDefaultPaymentMethod = () => {
  const queryClient = useQueryClient();

  return useMutation<
    BusinessSubscription,
    Error,
    StripeSetDefaultPaymentMethodDto
  >({
    mutationFn: (data) =>
      subscriptionService.setMyStripeDefaultPaymentMethod(data),
    onSuccess: async () => {
      // Immediate — subscription + saved card summary flip.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: subscriptionKeys.me() }),
        queryClient.invalidateQueries({
          queryKey: subscriptionKeys.myStripeDefaultPaymentMethod(),
        }),
        // Pending invoices + billing history + preview all depend on
        // subscription state — refetch so the CC button flip and the
        // wake-up-charged invoice appear without a page refresh.
        queryClient.invalidateQueries({ queryKey: ['customer-billing'] }),
        queryClient.invalidateQueries({ queryKey: ['self-billing'] }),
        // Synthetic "Borrador" row is driven by useMyNextInvoicePreview
        // (key `my-next-invoice-preview`) — not under customer-billing
        // / self-billing prefixes, so it needs its own invalidation
        // OR it survives the wake-up + keeps rendering "Pendiente".
        queryClient.invalidateQueries({ queryKey: NEXT_INVOICE_PREVIEW_KEY }),
      ]);
      // Delayed second pass — the wake-up hook fires the charge
      // fire-and-forget on the backend. Wait long enough for BOTH
      // the collection processor (Stripe API round-trip ~2s) AND
      // the delivery-processor cron (30s tick) to persist the
      // BILLING_PAYMENT_SUCCEEDED notification into the inbox.
      // 35s covers a fresh 30s cron cycle end-to-end.
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['customer-billing'] });
        queryClient.invalidateQueries({ queryKey: ['self-billing'] });
        queryClient.invalidateQueries({ queryKey: subscriptionKeys.me() });
        queryClient.invalidateQueries({ queryKey: NEXT_INVOICE_PREVIEW_KEY });
        queryClient.invalidateQueries({ queryKey: ['notification-inbox'] });
      }, 2500);
      // Second delayed pass at the 35s mark to pick up the
      // BILLING_PAYMENT_SUCCEEDED inapp delivery (delivery cron
      // ticks every 30s). Without this the bell stays cold until
      // the user reloads.
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['notification-inbox'] });
      }, 35_000);
    },
  });
};

export const usePayMySubscriptionWithStripe = () => {
  const queryClient = useQueryClient();

  return useMutation<SubscriptionPayment, Error, StripePayDto>({
    mutationFn: (data) => subscriptionService.payMySubscriptionWithStripe(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.me() });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
    },
  });
};

export const useCreateMyPaypalOrder = () => {
  return useMutation<PaypalCreateOrderResponseDto, Error, PaypalCreateOrderDto>(
    {
      mutationFn: (data) => subscriptionService.createMyPaypalOrder(data),
    }
  );
};

export const useCaptureMyPaypalOrder = () => {
  const queryClient = useQueryClient();

  return useMutation<SubscriptionPayment, Error, PaypalCaptureOrderDto>({
    mutationFn: (data) => subscriptionService.captureMyPaypalOrder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.me() });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
    },
  });
};

export const useConnectMyPaypal = () => {
  return useMutation<PaypalCreateOrderResponseDto, Error, void>({
    mutationFn: () => subscriptionService.connectMyPaypal(),
  });
};

export const useConfirmMyPaypalConnection = () => {
  const queryClient = useQueryClient();

  return useMutation<{ connected: boolean }, Error, string>({
    mutationFn: (orderId) =>
      subscriptionService.confirmMyPaypalConnection(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.me() });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
    },
  });
};

export const useActivateMyPaypalSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation<BusinessSubscription, Error, string>({
    mutationFn: (paypalSubscriptionId) =>
      subscriptionService.activateMyPaypalSubscription(paypalSubscriptionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      queryClient.invalidateQueries({
        queryKey: subscriptionKeys.myPaymentStatus(),
      });
      // Payments may need a moment to be recorded/repaired
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      }, 3000);
    },
  });
};

export const useRepairMyPaypalPayments = () => {
  const queryClient = useQueryClient();

  return useMutation<{ repaired: number }, Error, void>({
    mutationFn: () => subscriptionService.repairMyPaypalPayments(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
    },
  });
};

export const useBusinessSubscription = (
  businessId?: string,
  enabled = true
) => {
  return useQuery<BusinessSubscription>({
    queryKey: subscriptionKeys.business(businessId || 'none'),
    queryFn: () => {
      if (!businessId) {
        throw new Error('businessId is required');
      }
      return subscriptionService.getBusinessSubscription(businessId);
    },
    enabled: Boolean(businessId) && enabled,
  });
};

export const useBusinessStripeDefaultPaymentMethodSummary = (
  businessId?: string,
  enabled = true
) => {
  return useQuery<StripeDefaultPaymentMethodSummaryDto | null>({
    queryKey: subscriptionKeys.businessStripeDefaultPaymentMethod(
      businessId || 'none'
    ),
    queryFn: () => {
      if (!businessId) {
        throw new Error('businessId is required');
      }
      return subscriptionService.getBusinessStripeDefaultPaymentMethodSummary(
        businessId
      );
    },
    enabled: Boolean(businessId) && enabled,
  });
};

export const useBusinessSubscriptionPayments = (
  businessId?: string,
  params: SubscriptionPaymentsParams = {},
  enabled = true
) => {
  return useQuery<PaginatedResponse<SubscriptionPayment>>({
    queryKey: subscriptionKeys.businessPayments(businessId || 'none', params),
    queryFn: () => {
      if (!businessId) {
        throw new Error('businessId is required');
      }
      return subscriptionService.getBusinessPayments(businessId, params);
    },
    enabled: Boolean(businessId) && enabled,
  });
};

export const useBusinessAddons = (businessId?: string, enabled = true) => {
  return useQuery<AddonInfo>({
    queryKey: subscriptionKeys.businessAddons(businessId || 'none'),
    queryFn: () => {
      if (!businessId) {
        throw new Error('businessId is required');
      }
      return subscriptionService.getBusinessAddons(businessId);
    },
    enabled: Boolean(businessId) && enabled,
  });
};

export const useUpdateBusinessSubscription = (businessId: string) => {
  const queryClient = useQueryClient();

  return useMutation<
    BusinessSubscription,
    Error,
    UpdateBusinessSubscriptionDto
  >({
    mutationFn: (data) =>
      subscriptionService.updateBusinessSubscription(businessId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      queryClient.invalidateQueries({ queryKey: ['sadmin'] });
    },
  });
};

export const useCreateBusinessStripeSetupIntent = (businessId: string) => {
  return useMutation<StripeSetupIntentResponseDto, Error, void>({
    mutationFn: () =>
      subscriptionService.createBusinessStripeSetupIntent(businessId),
  });
};

export const useSetBusinessStripeDefaultPaymentMethod = (
  businessId: string
) => {
  const queryClient = useQueryClient();

  return useMutation<
    BusinessSubscription,
    Error,
    StripeSetDefaultPaymentMethodDto
  >({
    mutationFn: (data) =>
      subscriptionService.setBusinessStripeDefaultPaymentMethod(
        businessId,
        data
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      queryClient.invalidateQueries({
        queryKey:
          subscriptionKeys.businessStripeDefaultPaymentMethod(businessId),
      });
      queryClient.invalidateQueries({ queryKey: ['sadmin'] });
    },
  });
};

export const usePayBusinessSubscriptionWithStripe = (businessId: string) => {
  const queryClient = useQueryClient();

  return useMutation<SubscriptionPayment, Error, StripePayDto>({
    mutationFn: (data) =>
      subscriptionService.payBusinessSubscriptionWithStripe(businessId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      queryClient.invalidateQueries({ queryKey: ['sadmin'] });
    },
  });
};

export const useCreateBusinessPaypalOrder = (businessId: string) => {
  return useMutation<PaypalCreateOrderResponseDto, Error, PaypalCreateOrderDto>(
    {
      mutationFn: (data) =>
        subscriptionService.createBusinessPaypalOrder(businessId, data),
    }
  );
};

export const useCaptureBusinessPaypalOrder = (businessId: string) => {
  const queryClient = useQueryClient();

  return useMutation<SubscriptionPayment, Error, PaypalCaptureOrderDto>({
    mutationFn: (data) =>
      subscriptionService.captureBusinessPaypalOrder(businessId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      queryClient.invalidateQueries({ queryKey: ['sadmin'] });
    },
  });
};

export const useCreateBusinessSubscriptionPayment = (businessId: string) => {
  const queryClient = useQueryClient();

  return useMutation<SubscriptionPayment, Error, CreateSubscriptionPaymentDto>({
    mutationFn: (data) =>
      subscriptionService.createBusinessPayment(businessId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      queryClient.invalidateQueries({ queryKey: ['sadmin'] });
    },
  });
};

export const useCancelMySubscription = () => {
  return useMutation<
    {
      message: string;
      cancelledImmediately: boolean;
      providerCancellationFailed: boolean;
    },
    Error
  >({
    mutationFn: () => subscriptionService.cancelMySubscription(),
  });
};

export const useCancelMySubscriptionToFree = () => {
  const queryClient = useQueryClient();

  return useMutation<{ message: string }, Error>({
    mutationFn: () => subscriptionService.cancelMySubscriptionToFree(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
  });
};

// ── Payment failure status & retry ──────────────────────────────────

export const useMyPaymentStatus = (enabled = true) => {
  return useQuery<PaymentFailureStatus>({
    queryKey: subscriptionKeys.myPaymentStatus(),
    queryFn: () => subscriptionService.getMyPaymentStatus(),
    refetchInterval: 5 * 60 * 1000, // poll every 5 minutes
    staleTime: 2 * 60 * 1000,
    enabled,
  });
};

export const useRetryMyPayment = () => {
  const queryClient = useQueryClient();

  return useMutation<RetryPaymentResponse, Error>({
    mutationFn: () => subscriptionService.retryMyPayment(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: subscriptionKeys.myPaymentStatus(),
      });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.me() });
    },
  });
};

export const useMyAddons = (enabled = true) => {
  return useQuery<AddonInfo>({
    queryKey: [...subscriptionKeys.me(), 'addons'] as const,
    queryFn: () => subscriptionService.getMyAddons(),
    staleTime: 2 * 60 * 1000,
    enabled,
  });
};

/**
 * Quote a prepaid pack. Debounce the quantity before calling — this hits
 * the catalog tier table on every change.
 */
export const usePrepaidPackPreview = (quantity: number, enabled = true) =>
  useQuery({
    queryKey: [...subscriptionKeys.me(), 'prepaid-preview', quantity] as const,
    queryFn: () => subscriptionService.previewMyPrepaidPack(quantity),
    enabled: enabled && quantity > 0,
    staleTime: 60 * 1000,
  });

/**
 * Buy a prepaid e-CF pack.
 *
 * The idempotency key is generated HERE, once per mutation call, so a
 * double-click or a retry after a network blip reuses the same key and the
 * backend returns the original purchase instead of charging twice.
 *
 * Invalidates the addons query (the prepaid card) AND plan-usage (the
 * monthly gauge's headroom message) on success.
 */
export const usePurchaseMyPrepaidPack = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { quantity: number; idempotencyKey?: string }) =>
      subscriptionService.purchaseMyPrepaidPack({
        quantity: data.quantity,
        idempotencyKey: data.idempotencyKey ?? crypto.randomUUID(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...subscriptionKeys.me(), 'addons'] as const,
      });
      queryClient.invalidateQueries({
        queryKey: subscriptionKeys.myPlanUsageBase(),
      });
    },
  });
};

/**
 * Preview a scheduled reduction of `extra_*` addon slots.
 * Returns { currentMonthly, nextMonthly, delta, effectiveAt, currency }.
 * Never mutates state.
 */
export const usePreviewReduceMyAddon = () => {
  return useMutation({
    mutationFn: (data: {
      addonType:
        | 'extra_branch'
        | 'extra_user'
        | 'extra_warehouse'
        | 'extra_product'
        | 'extra_kds_screen'
        | 'extra_ecf';
      slotsToDrop: number;
    }) => subscriptionService.previewReduceMyAddon(data),
  });
};

/**
 * Reduce addon slots at end of current billing cycle. Backend
 * splits the newest matching row + sets `expiresAt =
 * currentPeriodEnd`. Recurring billing skips the row on the next
 * tick — no immediate refund needed. Invalidates plan-usage cache
 * + subscription-timeline so /account Capacidad + Historial refresh
 * immediately.
 */
export const useReduceMyAddon = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      addonType:
        | 'extra_branch'
        | 'extra_user'
        | 'extra_warehouse'
        | 'extra_product'
        | 'extra_kds_screen'
        | 'extra_ecf';
      slotsToDrop: number;
    }) => subscriptionService.reduceMyAddon(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: subscriptionKeys.myPlanUsageBase(),
      });
      queryClient.invalidateQueries({
        queryKey: [...subscriptionKeys.me(), 'addons'],
      });
      queryClient.invalidateQueries({
        queryKey: ['subscription-timeline'],
      });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.me() });
    },
  });
};

/**
 * Read-only preview of the charge for a capacity increase. Reactive
 * useQuery keyed on (addonType, quantity) so the confirm modal shows the
 * ACTUAL amount the operator will be billed — on an ANNUAL sub the
 * monthly rate prorated across the months remaining in the cycle, landing
 * on the next renewal invoice — as they adjust the quantity. Debounce
 * `quantity` at the call site to avoid a request per keystroke/slider tick.
 */
export const usePreviewPurchaseMyAddon = (
  addonType: PurchaseAddonDto['addonType'] | null,
  quantity: number,
  enabled: boolean
) => {
  return useQuery({
    queryKey: [
      ...subscriptionKeys.me(),
      'addon-purchase-preview',
      addonType,
      quantity,
    ],
    queryFn: () =>
      subscriptionService.previewPurchaseMyAddon({
        addonType: addonType as PurchaseAddonDto['addonType'],
        quantity,
      }),
    enabled: enabled && !!addonType && quantity > 0,
    staleTime: 30_000,
  });
};

export const usePurchaseMyAddon = () => {
  const queryClient = useQueryClient();
  return useMutation<PurchaseAddonResponse, Error, PurchaseAddonDto>({
    mutationFn: (data) => subscriptionService.purchaseMyAddon(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: subscriptionKeys.myPlanUsageBase(),
      });
      queryClient.invalidateQueries({
        queryKey: [...subscriptionKeys.me(), 'addons'],
      });
      // Refresh /account Historial de pagos + Próximo cobro so the
      // new addon charge appears immediately after purchase.
      queryClient.invalidateQueries({
        queryKey: [...subscriptionKeys.me(), 'payments'],
      });
      queryClient.invalidateQueries({
        queryKey: subscriptionKeys.me(),
      });
    },
  });
};

export const useCapturePaypalAddonOrder = () => {
  const queryClient = useQueryClient();
  return useMutation<PurchaseAddonResponse, Error, string>({
    mutationFn: (orderId) =>
      subscriptionService.capturePaypalAddonOrder(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: subscriptionKeys.myPlanUsageBase(),
      });
      queryClient.invalidateQueries({
        queryKey: [...subscriptionKeys.me(), 'addons'],
      });
    },
  });
};
